import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Input } from "../../../app/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../app/components/ui/form";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../../app/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../app/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../app/components/ui/tooltip";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { applyLaravelValidationErrors, getErrorMessage, getLaravelValidationErrors } from "../../../lib/laravelErrors";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";
import type { Brand } from "../hooks/useBrands";
import type { Category } from "../hooks/useCategories";
import { CategoryTreeSelect } from "./CategoryTreeSelect";
import { toast } from "sonner";

export type ProductFormValues = {
  name: string;
  sku: string;
  status: string;
  brand_id: string;
  category_ids: string[];
  barcode: string;
  is_gst_applicable: boolean;
  gst_type: "inclusive" | "exclusive";
  gst_rate_slab: "0" | "5" | "12" | "18" | "28" | "custom";
  gst_rate_custom: string;
  hsn_code: string;
};

export const NO_BRAND_VALUE = "__none__";

export function ProductForm({
  brands,
  categories,
  onSuccess,
  mode = "create",
  productId,
  initialValues,
}: {
  brands: Brand[];
  categories: Category[];
  onSuccess: () => void;
  mode?: "create" | "edit";
  productId?: string;
  initialValues?: Partial<ProductFormValues>;
}) {
  const { token } = useAuth();
  const [rootError, setRootError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const derivedInitialValues = useMemo<ProductFormValues>(() => {
    const gstApplicable =
      (initialValues as Partial<ProductFormValues> | undefined)?.is_gst_applicable ?? (mode === "create");
    const apiRate = (initialValues as { gst_rate?: string | null } | undefined)?.gst_rate;
    const slabs = ["0", "5", "12", "18", "28"] as const;
    let slab: ProductFormValues["gst_rate_slab"] = "18";
    let custom = "";
    if (apiRate != null && apiRate !== "") {
      const n = String(apiRate);
      if (slabs.includes(n as (typeof slabs)[number])) {
        slab = n as ProductFormValues["gst_rate_slab"];
      } else {
        slab = "custom";
        custom = n;
      }
    }

    return {
      name: initialValues?.name ?? "",
      sku: initialValues?.sku ?? "",
      status: initialValues?.status ?? "active",
      brand_id: initialValues?.brand_id ?? NO_BRAND_VALUE,
      category_ids: initialValues?.category_ids ?? [],
      barcode: (initialValues as any)?.barcode ?? "",
      is_gst_applicable: gstApplicable,
      gst_type:
        (initialValues as { gst_type?: string } | undefined)?.gst_type === "exclusive" ? "exclusive" : "inclusive",
      gst_rate_slab: slab,
      gst_rate_custom: custom,
      hsn_code: (initialValues as { hsn_code?: string } | undefined)?.hsn_code ?? "",
    };
  }, [initialValues, mode]);

  const form = useForm<ProductFormValues>({
    defaultValues: derivedInitialValues,
  });

  useEffect(() => {
    form.reset(derivedInitialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedInitialValues, mode, productId]);

  const watchedName = form.watch("name");
  const watchedCategoryIds = form.watch("category_ids");
  const watchedGstApplicable = form.watch("is_gst_applicable");
  const watchedSlab = form.watch("gst_rate_slab");
  const canSubmit = Boolean(watchedName?.trim() && Array.isArray(watchedCategoryIds) && watchedCategoryIds.length > 0);
  const watchedBarcode = form.watch("barcode");
  const hasBarcode = Boolean(watchedBarcode?.trim());

  async function onSubmit(values: ProductFormValues) {
    if (!token) return;
    setRootError(null);
    setSubmitting(true);
    try {
      let gstRate: number | null = null;
      if (values.is_gst_applicable) {
        if (values.gst_rate_slab === "custom") {
          const c = Number.parseFloat(values.gst_rate_custom);
          gstRate = Number.isFinite(c) ? c : null;
        } else {
          gstRate = Number.parseFloat(values.gst_rate_slab);
        }
      }

      const payload = {
        name: values.name.trim(),
        sku: values.sku.trim() || null,
        barcode: values.barcode.trim() || null,
        status: values.status.trim() || null,
        brand_id: values.brand_id && values.brand_id !== NO_BRAND_VALUE ? Number(values.brand_id) : null,
        category_ids: values.category_ids.map((x) => Number(x)),
        is_gst_applicable: values.is_gst_applicable,
        gst_type: values.is_gst_applicable ? values.gst_type : null,
        gst_rate: values.is_gst_applicable ? gstRate : null,
        hsn_code: values.hsn_code.trim() || null,
      };

      if (mode === "edit") {
        await api.products.update({
          token,
          id: productId ?? "",
          payload,
        });
        trackEvent(TRACKING_EVENTS.PRODUCT_UPDATED);
      } else {
        await api.products.create({
          token,
          payload,
        });
        trackEvent(TRACKING_EVENTS.PRODUCT_CREATED);
      }
      onSuccess();
    } catch (e) {
      const errors = getLaravelValidationErrors(e);
      if (errors) {
        applyLaravelValidationErrors(form.setError, errors);
        setRootError("Please fix the highlighted fields.");
        return;
      }
      setRootError(getErrorMessage(e, mode === "edit" ? "Failed to save product" : "Failed to create product"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {rootError ? (
        <Alert variant="destructive">
          <AlertTitle>{mode === "edit" ? "Save failed" : "Create failed"}</AlertTitle>
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="category_ids"
            rules={{
              validate: (v) => (Array.isArray(v) && v.length > 0 ? true : "Select at least one category"),
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Categories</FormLabel>
                <FormControl>
                  <CategoryTreeSelect categories={categories} value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="brand_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select brand (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_BRAND_VALUE}>No brand</SelectItem>
                      {brands.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Product name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sku"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SKU</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="barcode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Barcode</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <Input placeholder="active" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">GST configuration</h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline">
                    Help
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  GST will be applied during billing based on this product configuration.
                </TooltipContent>
              </Tooltip>
            </div>

            <FormField
              control={form.control}
              name="is_gst_applicable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Apply GST on this product</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <fieldset disabled={!watchedGstApplicable} className="space-y-4 min-w-0">
              <FormField
                control={form.control}
                name="gst_rate_slab"
                rules={{
                  validate: (v, fv) => {
                    if (!fv.is_gst_applicable) return true;
                    if (v !== "custom") return true;
                    const n = Number.parseFloat(fv.gst_rate_custom);
                    return Number.isFinite(n) && n >= 0 ? true : "Enter a valid custom GST %";
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GST rate</FormLabel>
                    <FormControl>
                      <RadioGroup
                        className="flex flex-wrap gap-3"
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        {(["0", "5", "12", "18", "28"] as const).map((s) => (
                          <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                            <RadioGroupItem value={s} id={`gst-${s}`} />
                            <span>{s}%</span>
                          </label>
                        ))}
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <RadioGroupItem value="custom" id="gst-custom" />
                          <span>Custom</span>
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchedSlab === "custom" ? (
                <FormField
                  control={form.control}
                  name="gst_rate_custom"
                  rules={{
                    validate: (v, fv) => {
                      if (!fv.is_gst_applicable || fv.gst_rate_slab !== "custom") return true;
                      const n = Number.parseFloat(String(v));
                      return Number.isFinite(n) && n >= 0 ? true : "Enter a valid number";
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Custom rate (%)</FormLabel>
                      <FormControl>
                        <Input inputMode="decimal" placeholder="e.g. 3" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <FormField
                control={form.control}
                name="gst_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GST type</FormLabel>
                    <FormControl>
                      <RadioGroup className="flex flex-col gap-2" value={field.value} onValueChange={field.onChange}>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <RadioGroupItem value="inclusive" id="gst-inc" />
                          <span>Inclusive (price includes GST)</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <RadioGroupItem value="exclusive" id="gst-exc" />
                          <span>Exclusive (GST added on top)</span>
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hsn_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>HSN code</FormLabel>
                    <FormControl>
                      <Input placeholder="Optional" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>
          </div>

          <div className="flex items-center justify-end gap-2">
            {mode === "edit" && productId ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting || hasBarcode}
                  onClick={() => {
                    if (!token) return;
                    void api.products
                      .generateBarcode({ token, id: productId })
                      .then((res) => {
                        form.setValue("barcode", res.barcode, { shouldDirty: true });
                        if (res.already_exists) toast.message("Barcode already exists");
                        else toast.success("Barcode generated successfully");
                      })
                      .catch((e) => {
                        toast.error(e instanceof Error ? e.message : "Failed to generate barcode");
                      });
                  }}
                >
                  Generate Barcode
                </Button>
                {hasBarcode ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const b = watchedBarcode.trim();
                      if (!b) return;
                      void navigator.clipboard.writeText(b);
                      toast.success("Copied");
                    }}
                  >
                    Copy
                  </Button>
                ) : null}
              </>
            ) : null}
            <Button type="submit" disabled={!canSubmit || submitting}>
              {submitting ? (mode === "edit" ? "Saving..." : "Creating...") : mode === "edit" ? "Save changes" : "Create product"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

